import { Injectable, signal, effect, inject } from '@angular/core';
import { StringOutputParser } from '@langchain/core/output_parsers';
import { ChatPromptTemplate } from '@langchain/core/prompts';
import { ChatOpenAI } from '@langchain/openai';
import { assistant, textToSpeech } from '@runnables';
import { OpenAI } from 'openai';
import { AppService, TaskService } from '@services';

const OPEN_API_KEY =
  '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';

@Injectable({
  providedIn: 'root',
})
export class AiXpertService {
  #appService = inject(AppService);
  #taskService = inject(TaskService);

  constructor() {
    textToSpeech(
      'Hi! I am your AI-powered suturing coach—here to guide you with real-time feedback, personalized tips, and smart support. Let’s refine your and improve your technique.',
      this.openAi,
    )
      .invoke(this.answer())
      .then(async (audioBlob) => {
        this.startAudioUrl = URL.createObjectURL(audioBlob.blob);
      });

    setInterval(() => {
      console.log('Sending collected messages...');
      if (this.messageCounter < 30 && this.#appService.showSimXpert()) {
        this.sendMessage(this.collectedMessages);
        this.messageCounter++;
        this.idle.set(true);
      }
      this.collectedMessages = '';
    }, 24000);

    effect(() => {
      if (this.#appService.showSimXpert() && this.startAudioUrl !== null) {
        this.audio = new Audio(this.startAudioUrl);
        this.audio.play();
        this.startAudioUrl = null;
      }
    });

    effect(
      () => {
        if (this.messageCounter === 0 && this.#appService.showSimXpert()) {
          console.log('Sending collected messages...');
          this.sendMessage(this.collectedMessages);
          this.messageCounter++;
          this.idle.set(true);
        }
      },
      { allowSignalWrites: true },
    );
  }

  collectedMessages = '';
  startAudioUrl: string | null = '';
  audio: HTMLAudioElement;
  messageCounter = 0;
  idle = signal(false);

  answer = signal(
    'Hi! I am your AI-powered suturing coach—here to guide every stitch with real-time feedback, personalized tips, and smart support. Let’s refine your and improve your technique.',
  );
  title = signal('Welcome to AI Xpert!');

  chatOpenAi = new ChatOpenAI({
    apiKey: OPEN_API_KEY,
  });

  openAi = new OpenAI({
    apiKey: OPEN_API_KEY,
    dangerouslyAllowBrowser: true,
  });

  sendXperMessage(messages: string) {
    const uniqueLines = [...new Set(messages.split('\n'))].join('\n');
    console.log(uniqueLines);
    this.collectedMessages = this.collectedMessages + uniqueLines + '\n';
  }

  async sendMessage(classification: string) {
    const prompt = ChatPromptTemplate.fromTemplate(
      `You are a suturing expert and giving feedback to the procedure,  Give recommandations what to do next based on this feedback: ${classification}. 
      Answer in a friendly supportive manner.
      This is the tasklist:
      Prepare the iPad and tissue
      Put the suture on top of the wound
      Tie a knot
      Lock the first stitch
      Close in a continous suturing pattern
      Close the rest of the wound
      Cut the suture
      The current task is : ${this.#taskService.currentTask().description}.
      answer only with stuff releated to the task.
      If ${classification} contains "Good technique", especially mention it.
      If ${classification} contains "Mistake", especially mention it.
      Avoid repeating yourself with previous responses. Stay silent if you have nothing new to say.
      Response should have 20 words in maximum.
      Respond only with a JSON object in the following format: {{ "title": "string","text": "string"}}
      title property should contain maximal three word medical procedure summary of the response. The text should be a detailed response to the question. Do not include any other text or explanation outside of the JSON object.
     
      ###
      You are reviewing a video of a suturing exercise. The task involves closing a vertical wound on practice tissue using two needle drivers, a curved black suture, and scissors. Your goal is to evaluate the technique and execution and checking these questions:

      Setup:

      Is the iPad positioned to provide a clear, stable, and well-lit view of the entire wound throughout the procedure?

      Is the wound centered in the frame with no obstructions?

      Initial Stitch and Knot:

        Was the needle gripped correctly (two-thirds from the tip)?

        Was the first bite placed left-to-right across the wound accurately?

        Was the knot tied securely and correctly (double knot, tension appropriate to approximate tissue without damage)?

        Was the short tail cut cleanly after knotting?

      First Locking Stitch:

        Was the locking stitch placed properly below the knot to anchor the suture line?

        Was it close enough to the wound edge?

    Continuous Suturing:

        Were consistent, smooth stitches made down the wound?

        Was proper technique used (needle entry/exit near wound edges, avoiding wide bites)?

        Were instruments kept uncrossed and the view unobstructed (hands out of frame)?

        Was suture tension consistent and appropriate?

    Finishing:

        Was the suture cut neatly after the wound was fully closed?

    Please comment on strengths, areas for improvement, and any noticeable deviations from the expected technique.
     `,
    );

    const thread = await this.openAi.beta.threads.create();

    const composedChain = prompt
      .pipe((prompt) => {
        return prompt.messages[0].content;
      })
      .pipe((prompt: any) => assistant(prompt, this.openAi, thread))
      .pipe((response: any) => {
        return new StringOutputParser().invoke(response);
      });

    const responseStream = await composedChain.stream({ classification });

    this.answer.set('');
    this.title.set('');

    let response = '';

    for await (const chunk of responseStream) {
      response = response + chunk;
    }

    const responseObj: { title: string; text: string } = JSON.parse(response);

    this.title.set(responseObj.title);
    this.answer.set(responseObj.text);

    textToSpeech(this.answer(), this.openAi)
      .invoke(this.answer())
      .then(async (audioBlob) => {
        const audioUrl = URL.createObjectURL(audioBlob.blob);
        if (this.audio && !this.audio.paused) {
          this.audio.pause();
          this.audio.src = audioUrl;
        } else {
          this.audio = new Audio(audioUrl);
        }
        this.audio.currentTime = 0;
        const ctx = new AudioContext();
        fetch(audioUrl)
          .then((res) => res.arrayBuffer())
          .then((buffer) => ctx.decodeAudioData(buffer))
          .then((decoded) => {
            const source = ctx.createBufferSource();
            source.buffer = decoded;
            source.connect(ctx.destination);
            source.start();
          });
        this.idle.set(false);
      });
  }
}
